import { useEffect, useState } from "react";
import { Plus, Search } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";
import api, { dateTimeIt } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const KINDS = [
  { value: "", key: "memory.all" },
  { value: "quote", key: "nav.quotes" },
  { value: "invoice", key: "nav.invoices" },
  { value: "cliente", key: "nav.clients" },
  { value: "task", key: "agenda.task" },
  { value: "nota", key: "memory.notes" },
];

const KIND_STYLE = {
  quote: "bg-blue-50 text-blue-700",
  invoice: "bg-emerald-50 text-emerald-700",
  cliente: "bg-indigo-50 text-indigo-700",
  task: "bg-amber-50 text-amber-700",
  nota: "bg-slate-100 text-slate-700",
  agenda: "bg-violet-50 text-violet-700",
};

const Memoria = () => {
  const { t, ts } = useI18n();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [kind, setKind] = useState("");
  const [note, setNote] = useState("");

  const load = () =>
    api.get("/activities", { params: { q: q || undefined, kind: kind || undefined } })
      .then(({ data }) => setItems(data));

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [q, kind]);

  const addNote = async (e) => {
    e.preventDefault();
    if (!note.trim()) return;
    await api.post("/activities", { kind: "nota", title: note, detail: "" });
    setNote("");
    toast.success(t("memory.noteSaved"));
    load();
  };

  return (
    <div className="space-y-5" data-testid="memory-page">
      <form onSubmit={addNote} className="card-flat flex flex-wrap gap-3 p-5">
        <Input
          data-testid="memory-note-input"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder={t("memory.notePlaceholder")}
          className="flex-1 min-w-[240px] focus:ring-2 focus:ring-blue-600"
        />
        <Button data-testid="memory-add-note" type="submit" className="gap-2 bg-blue-600 text-white hover:bg-blue-700">
          <Plus className="h-4 w-4" /> {t("memory.register")}
        </Button>
      </form>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            data-testid="memory-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("memory.searchPlaceholder")}
            className="pl-9 focus:ring-2 focus:ring-blue-600"
          />
        </div>
        <div className="flex flex-wrap gap-1">
          {KINDS.map((k) => (
            <button
              key={k.value}
              data-testid={`memory-filter-${k.value || "all"}`}
              onClick={() => setKind(k.value)}
              className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-colors duration-200 ${
                kind === k.value ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-100"
              }`}
            >
              {t(k.key)}
            </button>
          ))}
        </div>
      </div>

      <div className="card-flat p-6" data-testid="memory-timeline">
        {items.length === 0 && (
          <p className="py-10 text-center text-sm text-slate-500">{t("memory.empty")}</p>
        )}
        <ol className="relative space-y-5 border-l border-slate-200 pl-6">
          {items.map((a, i) => (
            <li key={a.id} className="rise relative" style={{ animationDelay: `${i * 40}ms` }}>
              <span className="absolute -left-[31px] top-1.5 h-2.5 w-2.5 rounded-full bg-blue-600" />
              <div className="flex flex-wrap items-center gap-2">
                <span className={`rounded px-2 py-0.5 text-xs font-semibold ${KIND_STYLE[a.kind] || "bg-slate-100"}`}>
                  {ts(a.kind)}
                </span>
                <span className="font-mono-num text-xs text-slate-500">{dateTimeIt(a.created_at)}</span>
              </div>
              <p className="mt-1 text-sm font-medium">{a.title}</p>
              {a.detail && <p className="text-sm text-slate-500">{a.detail}</p>}
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
};

export default Memoria;
